"use client"

import { useState, useEffect } from "react"
import "./ProductCategoryManager.css"
import productCategoryService from "../../../services/api/productCategoryService"

export default function ProductCategoryManagement() {
    // State for categories list
    const [categories, setCategories] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")

    // State for form inputs
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        photo: "/placeholder.svg?height=100&width=300"
    })

    // State for views
    const [selectedCategory, setSelectedCategory] = useState(null)
    const [showDetail, setShowDetail] = useState(false)
    const [showAddForm, setShowAddForm] = useState(false)
    const [isEditing, setIsEditing] = useState(false)

    // Fetch categories on component mount
    useEffect(() => {
        fetchCategories()
    }, [])

    // Fetch all categories
    const fetchCategories = async () => {
        try {
            setLoading(true)
            const response = await productCategoryService.getAllCategories()
            setCategories(Array.isArray(response) ? response : [])
        } catch (error) {
            console.error("Error fetching categories:", error)
            setError("Không thể tải danh sách danh mục")
            setCategories([])
        } finally {
            setLoading(false)
        }
    }

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData({
            ...formData,
            [name]: value,
        })
    }

    // Handle image upload
    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const reader = new FileReader()
            reader.onload = (event) => {
                if (event.target && event.target.result) {
                    setFormData({
                        ...formData,
                        photo: event.target.result.toString(),
                    })
                }
            }
            reader.readAsDataURL(e.target.files[0])
        }
    }

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            setLoading(true)
            if (isEditing && selectedCategory) {
                await productCategoryService.updateCategory(selectedCategory._id, formData)
            } else {
                await productCategoryService.createCategory(formData)
            }
            await fetchCategories()
            // Reset form and states
            setFormData({
                name: "",
                description: "",
                photo: "/placeholder.svg?height=100&width=300"
            })
            setShowAddForm(false)
            setIsEditing(false)
            setSelectedCategory(null)
        } catch (error) {
            console.error("Error saving category:", error)
            setError(isEditing ? "Không thể cập nhật danh mục" : "Không thể tạo danh mục mới")
        } finally {
            setLoading(false)
        }
    }

    // Handle category deletion
    const handleDelete = async (id) => {
        if (window.confirm("Bạn có chắc chắn muốn xóa danh mục này không?")) {
            try {
                setLoading(true)
                await productCategoryService.deleteCategory(id)
                await fetchCategories()
                if (selectedCategory && selectedCategory._id === id) {
                    setShowDetail(false)
                    setSelectedCategory(null)
                }
            } catch (error) {
                console.error("Error deleting category:", error)
                setError("Không thể xóa danh mục")
            } finally {
                setLoading(false)
            }
        }
    }

    // Handle view details
    const handleViewDetails = async (category) => {
        try {
            setLoading(true)
            console.log('Fetching details for category:', category)
            const categoryDetails = await productCategoryService.getCategoryById(category._id)
            console.log('Received category details:', categoryDetails)
            setSelectedCategory(categoryDetails)
            setShowDetail(true)
        } catch (error) {
            console.error("Error fetching category details:", error)
            console.error("Error details:", error.response?.data || error.message)
            setError("Không thể tải thông tin chi tiết danh mục")
        } finally {
            setLoading(false)
        }
    }

    // Handle edit category
    const handleEdit = (category) => {
        setFormData({
            name: category.name,
            description: category.description,
            photo: category.photo || "/placeholder.svg?height=100&width=300"
        })
        setSelectedCategory(category)
        setIsEditing(true)
        setShowAddForm(true)
        setShowDetail(false)
    }

    // Toggle add form
    const toggleAddForm = () => {
        setShowAddForm(!showAddForm)
        if (!showAddForm) {
            // Reset form when opening
            setFormData({
                name: "",
                description: "",
                photo: "/placeholder.svg?height=100&width=300"
            })
            setIsEditing(false)
            setSelectedCategory(null)
        }
    }

    return (
        <div className="pcm-container">
            {!showDetail ? (
                <>
                    <h1 className="pcm-heading">Quản Lý Danh Mục Sản Phẩm</h1>
                    {error && <div className="error-message">{error}</div>}
                    {loading && <div className="loading-spinner">Đang tải...</div>}
                    <div className="pcm-layout">
                        <div className="pcm-list-container">
                            <div className="pcm-list-header">
                                <h2 className="pcm-subheading">Danh Sách Danh Mục</h2>
                                <button className="pcm-add-button" onClick={toggleAddForm} disabled={loading}>
                                    {showAddForm ? "Ẩn Form" : "Thêm Danh Mục Mới"}
                                </button>
                            </div>
                            <div className="pcm-list">
                                {Array.isArray(categories) && categories.length > 0 ? (
                                    categories.map((category) => (
                                        <div key={category._id} className="pcm-card">
                                            <div className="pcm-image">
                                                <img src={category.photo || "/placeholder.svg"} alt={`Ảnh ${category.name}`} />
                                            </div>
                                            <div className="pcm-details">
                                                <h3>{category.name}</h3>
                                                <p><strong>Mô tả:</strong> {category.description}</p>
                                                <div className="pcm-actions">
                                                    <button className="pcm-view-button" onClick={() => handleViewDetails(category)} disabled={loading}>
                                                        Xem Chi Tiết
                                                    </button>
                                                    <button className="pcm-edit-button" onClick={() => handleEdit(category)} disabled={loading}>
                                                        Sửa
                                                    </button>
                                                    <button className="pcm-delete-button" onClick={() => handleDelete(category._id)} disabled={loading}>
                                                        Xóa
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-categories">Không tìm thấy danh mục nào</div>
                                )}
                            </div>
                        </div>

                        {showAddForm && (
                            <div className="pcm-add-section">
                                <div className="pcm-add-card">
                                    <h2 className="pcm-subheading">{isEditing ? "Sửa Danh Mục" : "Thêm Danh Mục Mới"}</h2>
                                    <form onSubmit={handleSubmit} className="pcm-form">
                                        <div className="pcm-form-group">
                                            <label htmlFor="name">Tên Danh Mục</label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>

                                        <div className="pcm-form-group">
                                            <label htmlFor="description">Mô tả</label>
                                            <textarea
                                                id="description"
                                                name="description"
                                                value={formData.description}
                                                onChange={handleInputChange}
                                                rows="4"
                                                required
                                            />
                                        </div>

                                        <div className="pcm-form-group">
                                            <label htmlFor="photo">Ảnh Danh Mục</label>
                                            <input
                                                type="file"
                                                id="photo"
                                                name="photo"
                                                onChange={handleImageChange}
                                                accept="image/*"
                                            />
                                            <div className="pcm-image-preview">
                                                <img src={formData.photo} alt="Xem trước ảnh danh mục" />
                                            </div>
                                        </div>

                                        <div className="pcm-form-actions">
                                            <button type="button" className="pcm-cancel-button" onClick={toggleAddForm}>
                                                Hủy
                                            </button>
                                            <button type="submit" className="pcm-submit-button" disabled={loading}>
                                                {loading ? "Đang lưu..." : (isEditing ? "Cập Nhật" : "Thêm Danh Mục")}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <div className="pcm-detail-section">
                    <button className="pcm-back-button" onClick={() => setShowDetail(false)} disabled={loading}>Quay Lại</button>
                    <div className="pcm-detail-content">
                        <div className="pcm-detail-image">
                            <img src={selectedCategory.photo || "/placeholder.svg"} alt={`Ảnh ${selectedCategory.name}`} />
                        </div>
                        <div className="pcm-detail-info">
                            <h2>{selectedCategory.name}</h2>
                            <p><strong>Mô tả:</strong> {selectedCategory.description}</p>
                            <div className="pcm-detail-actions">
                                <button className="pcm-edit-button" onClick={() => handleEdit(selectedCategory)} disabled={loading}>
                                    Sửa
                                </button>
                                <button className="pcm-delete-button" onClick={() => { handleDelete(selectedCategory._id); setShowDetail(false); }} disabled={loading}>
                                    Xóa
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
} 